'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { useSiteContent, useSectionVisible, useContentLoading } from '@/lib/content/SiteContentContext';

const FALLBACK = 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=1920';
const SLIDE_DURATION = 6000;

const HeroSection: React.FC = () => {
  const heroTitle    = useSiteContent('hero_title',       'Discover Your Signature Scent');
  const heroSubtitle = useSiteContent('hero_subtitle',    'Explore our curated collection of luxury perfumes crafted by master perfumers from around the world');
  const heroButton   = useSiteContent('hero_button_text', 'Explore Collection');
  const visible        = useSectionVisible('hero');
  const contentLoading = useContentLoading();

  const [images, setImages]             = useState<string[]>([]);
  const [loaded, setLoaded]             = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Per-slide activation counter.
  // Increments ONLY for the slide that just became active.
  // → that slide's inner div remounts (restarting kenburns from scale 1.08).
  // → every OTHER slide's key stays the same → no remount → no snap.
  const [activeCounts, setActiveCounts] = useState<number[]>([]);

  useEffect(() => {
    async function load() {
      const supabase = createClient();
      const { data: products } = await supabase
        .from('products')
        .select('id, image_url')
        .eq('is_active', true)
        .eq('is_featured', true)
        .limit(8);

      if (!products?.length) return;

      const ids = products.map((p) => p.id);
      const { data: media } = await supabase
        .from('product_media')
        .select('product_id, url')
        .in('product_id', ids)
        .eq('type', 'image')
        .order('sort_order', { ascending: true });

      const mediaMap: Record<string, string> = {};
      for (const m of media ?? []) {
        if (!mediaMap[m.product_id]) mediaMap[m.product_id] = m.url;
      }

      const urls = products
        .map((p) => mediaMap[p.id] || p.image_url)
        .filter(Boolean) as string[];

      if (urls.length) setImages(urls);
    }
    load().finally(() => setLoaded(true));
  }, []);

  // Only the newly active slide gets its counter incremented
  useEffect(() => {
    setActiveCounts((prev) => {
      const next = [...prev];
      next[currentIndex] = (next[currentIndex] || 0) + 1;
      return next;
    });
  }, [currentIndex]);

  // Auto-advance
  useEffect(() => {
    if (images.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((i) => (i + 1) % images.length);
    }, SLIDE_DURATION);
    return () => clearInterval(timer);
  }, [images.length]);

  if (!visible) return null;

  // Don't show any image (not even the fallback) until fetch completes — prevents
  // the 1-second flash of the old placeholder image on every page load/refresh.
  const slides = loaded ? (images.length > 0 ? images : [FALLBACK]) : [];

  return (
    <section data-component-id="src/app/home/components/HeroSection.tsx:87:4" data-component-path="src/app/home/components/HeroSection.tsx" data-component-line="87" data-component-end-line="167" data-component-file="HeroSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22relative%20h-%5B600px%5D%20w-full%20overflow-hidden%20bg-black%22%7D" className="relative h-[600px] w-full overflow-hidden bg-black">

      {slides.map((img, i) => {
        const isActive = i === currentIndex;
        return (
          <div data-component-id="src/app/home/components/HeroSection.tsx:92:10" data-component-path="src/app/home/components/HeroSection.tsx" data-component-line="92" data-component-end-line="119" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%22%7D"
            key={i}
            className="absolute inset-0"
            style={{
              opacity: isActive ? 1 : 0,
              transition: 'opacity 1.8s ease-in-out',
              zIndex: isActive ? 1 : 0,
              animation: i === 0 ? 'fadeIn 0.8s ease-in forwards' : undefined,
            }}
          >
            {/*
              key = activeCounts[i]: changes only when slide i becomes active.
              Inactive slides keep the same key → no remount → no scale snap.
              animationPlayState: inactive slides PAUSE the kenburns right where
              they are → they freeze at their current scale while fading out,
              with zero visual glitch.
            */}
            <div data-component-id="src/app/home/components/HeroSection.tsx:109:12" data-component-path="src/app/home/components/HeroSection.tsx" data-component-line="109" data-component-end-line="118" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-cover%20bg-center%22%7D"
              key={activeCounts[i] || 0}
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(${img})`,
                animation: 'kenburns 9s ease-out forwards',
                animationPlayState: isActive ? 'running' : 'paused',
                willChange: 'transform',
              }}
            />
          </div>
        );
      })}

      {/* Overlay */}
      <div data-component-id="src/app/home/components/HeroSection.tsx:124:6" data-component-path="src/app/home/components/HeroSection.tsx" data-component-line="124" data-component-end-line="124" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-black%2F50%22%7D" className="absolute inset-0 bg-black/50" style={{ zIndex: 2 }} />

      {/* Text — fades in after CMS content resolves to prevent default→actual text glitch */}
      <div data-component-id="src/app/home/components/HeroSection.tsx:127:6" data-component-path="src/app/home/components/HeroSection.tsx" data-component-line="127" data-component-end-line="147" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20mx-auto%20flex%20h-full%20max-w-%5B1440px%5D%20flex-col%20items-center%20justify-center%20px-4%20text-center%20md%3Apx-6%20lg%3Apx-8%22%7D"
        className="relative mx-auto flex h-full max-w-[1440px] flex-col items-center justify-center px-4 text-center md:px-6 lg:px-8"
        style={{
          zIndex: 3,
          opacity: contentLoading ? 0 : 1,
          transition: 'opacity 0.4s ease-in',
        }}
      >
        <h1 data-component-id="src/app/home/components/HeroSection.tsx:135:8" data-component-path="src/app/home/components/HeroSection.tsx" data-component-line="135" data-component-end-line="137" data-component-file="HeroSection.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22mb-6%20font-heading%20text-5xl%20font-bold%20text-white%20md%3Atext-6xl%20lg%3Atext-7xl%22%7D" className="mb-6 font-heading text-5xl font-bold text-white md:text-6xl lg:text-7xl">
          {heroTitle}
        </h1>
        <p data-component-id="src/app/home/components/HeroSection.tsx:138:8" data-component-path="src/app/home/components/HeroSection.tsx" data-component-line="138" data-component-end-line="140" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mb-8%20max-w-2xl%20font-body%20text-lg%20text-white%2F80%20md%3Atext-xl%22%7D" className="mb-8 max-w-2xl font-body text-lg text-white/80 md:text-xl">
          {heroSubtitle}
        </p>
        <Link data-component-id="src/app/home/components/HeroSection.tsx:141:8" data-component-path="src/app/home/components/HeroSection.tsx" data-component-line="141" data-component-end-line="146" data-component-file="HeroSection.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fshop-catalog%22%2C%22className%22%3A%22rounded-lg%20bg-primary%20px-8%20py-4%20font-body%20text-base%20font-medium%20text-primary-foreground%20shadow-luxury%20transition-luxury%20hover%3Abg-primary%2F90%20hover%3Ashadow-luxury-md%22%7D"
          href="/shop-catalog"
          className="rounded-lg bg-primary px-8 py-4 font-body text-base font-medium text-primary-foreground shadow-luxury transition-luxury hover:bg-primary/90 hover:shadow-luxury-md"
        >
          {heroButton}
        </Link>
      </div>

      {/* Dot indicators */}
      {slides.length > 1 && (
        <div data-component-id="src/app/home/components/HeroSection.tsx:151:8" data-component-path="src/app/home/components/HeroSection.tsx" data-component-line="151" data-component-end-line="165" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-6%20left-1%2F2%20flex%20-translate-x-1%2F2%20gap-2%22%7D"
          className="absolute bottom-6 left-1/2 flex -translate-x-1/2 gap-2"
          style={{ zIndex: 3 }}
        >
          {slides.map((_, i) => (
            <button data-component-id="src/app/home/components/HeroSection.tsx:156:12" data-component-path="src/app/home/components/HeroSection.tsx" data-component-line="156" data-component-end-line="163" data-component-file="HeroSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={i}
              onClick={() => setCurrentIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === currentIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/40'
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default HeroSection;
